import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { createTalent, updateTalent } from '../../../services/talent';
import { uploadImage } from '../../../services/upload';
import './TalentForm.css';

const TalentForm = ({ talent, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    experience: '',
    rate: '',
    bio: '',
    skills: '',
    portfolio: '',
    image: '',
    available: true,
  });

  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (talent) {
      setFormData(talent);
      setImagePreview(talent.image || '');
    }
  }, [talent]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    const result = await uploadImage(file);

    if (result.success) {
      setFormData({ ...formData, image: result.url });
      setImagePreview(result.url);
    } else {
      alert('Upload failed: ' + (result.error || 'Unknown error'));
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: '' });
    setImagePreview('');
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    if (!formData.experience.trim()) newErrors.experience = 'Experience is required';
    if (!formData.rate.trim()) newErrors.rate = 'Rate is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    const result = talent
      ? await updateTalent(talent.id, formData)
      : await createTalent(formData);

    setLoading(false);

    if (result.success) {
      onSave();
      onClose();
    } else {
      alert('Error saving talent: ' + result.error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="talent-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {talent ? 'Edit Talent' : 'Add New Talent'}
          </h2>
          <button onClick={onClose} className="modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="talent-form">
          <div className="form-scroll">
            {/* Basic Info */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="John Doe"
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select Category</option>
                    <option value="Actor">Actor</option>
                    <option value="Director">Director</option>
                    <option value="Cinematographer">Cinematographer</option>
                    <option value="Producer">Producer</option>
                    <option value="Editor">Editor</option>
                    <option value="Sound Designer">Sound Designer</option>
                    <option value="Makeup Artist">Makeup Artist</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.category && <span className="form-error">{errors.category}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Experience *</label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="5+ years"
                  />
                  {errors.experience && <span className="form-error">{errors.experience}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Rate ($/day) *</label>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="500"
                  />
                  {errors.rate && <span className="form-error">{errors.rate}</span>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    name="available"
                    checked={formData.available}
                    onChange={handleChange}
                    className="form-checkbox"
                  />
                  <span>Available for booking</span>
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="4"
                  placeholder="Brief biography..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Skills</label>
                <input
                  type="text"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Acting, Voice Over, Improv"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Portfolio URL</label>
                <input
                  type="url"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://portfolio.com"
                />
              </div>
            </div>

            {/* Image */}
            <div className="form-section">
              <h3 className="form-section-title">Photo</h3>
              
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="btn btn-ghost btn-sm"
                  >
                    Remove Photo
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">Upload Photo</label>
                <div className="upload-container">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="upload-input"
                    disabled={uploading}
                  />
                  <label htmlFor="image-upload" className="upload-label">
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Choose Photo to Upload'}
                  </label>
                </div>
                <p className="form-help">
                  Upload photo (JPG, PNG, GIF, WebP - Max 5MB)
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Or Enter Photo URL</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://example.com/photo.jpg"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (talent ? 'Update Talent' : 'Add Talent')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TalentForm;